import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import InformationTable from '../../../components/Table/InformationTable'
import TableHeader from '../../../components/TableHeader/TableHeader'
import PageLoading from '../../../components/PageLoading/PageLoading'
import {
  buscarPorEmailAlumno,
  buscarPorTelefonoAlumno,
  buscarPorEmailLead,
  buscarPorTelefonoLead
} from '../../../utils/api/buscadorUniversal'
import { getEstado } from '../../../utils/model/leads'
import css from './Buscador.module.css'

const normalizeEmail = value => (value || '').trim().replace(/\s+/g, '')
const normalizePhone = value => (value || '').trim()

const Buscador = () => {
  const navigate = useNavigate()
  const notification = useNotification()

  const [tipo, setTipo] = useState('email')
  const [input, setInput] = useState('')
  const [busqueda, setBusqueda] = useState(null)

  const tipoBusqueda = busqueda?.tipo
  const valorBusqueda = busqueda?.valor

  const { isFetching: isLoadingAlumnos, data: alumnosResultado = [] } =
    useQuery({
      queryKey: ['alumno', 'list', 'search', tipoBusqueda, valorBusqueda],
      enabled: !!busqueda,
      queryFn: () => {
        const buscar =
          tipoBusqueda === 'email'
            ? buscarPorEmailAlumno
            : buscarPorTelefonoAlumno
        return buscar(valorBusqueda)
          .then(datos => datos || [])
          .catch(err => {
            notification.error({
              title: 'Error al recuperar los alumnos',
              content: err
            })
            return []
          })
      }
    })

  const { isFetching: isLoadingLeads, data: leadsResultado = [] } = useQuery({
    queryKey: ['feedback', 'list', 'search', tipoBusqueda, valorBusqueda],
    enabled: !!busqueda,
    queryFn: () => {
      const buscar =
        tipoBusqueda === 'email' ? buscarPorEmailLead : buscarPorTelefonoLead
      return buscar(valorBusqueda)
        .then(datos => datos || [])
        .catch(err => {
          notification.error({
            title: 'Error al recuperar los leads',
            content: err
          })
          return []
        })
    }
  })

  const handleTipoChange = (_, newTipo) => {
    if (newTipo === null || newTipo === tipo) return
    setTipo(newTipo)
    setInput('')
  }

  const handleBuscar = () => {
    const normalize = tipo === 'email' ? normalizeEmail : normalizePhone
    const valor = normalize(input)
    if (!valor) return
    setBusqueda({ tipo, valor })
  }

  const handleInputKeyDown = e => {
    if (e.key === 'Enter') handleBuscar()
  }

  const isLoading = isLoadingAlumnos || isLoadingLeads
  const hasResultados = !!busqueda && !isLoading

  return (
    <PageLoading>
      <TableHeader
        showSearch={false}
        title='Buscador universal'
        actions={
          <div className={css.actions}>
            <div className={css.grupoBusqueda}>
              <TextField
                className={css.input}
                variant='standard'
                size='small'
                label='Buscar'
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
              />
              <ToggleButtonGroup
                className={css.tipo}
                color='primary'
                size='small'
                exclusive
                value={tipo}
                onChange={handleTipoChange}
              >
                <ToggleButton className={css.toggleButton} value='email'>
                  <i className={`material-icons ${css.toggleIcon}`}>mail</i>
                </ToggleButton>
                <ToggleButton className={css.toggleButton} value='telefono'>
                  <i className={`material-icons ${css.toggleIcon}`}>phone</i>
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div className={css.grupoAcciones}>
              <Button
                className={css.button}
                variant='contained'
                color='primary'
                size='medium'
                onClick={handleBuscar}
                disabled={!input.trim() || isLoading}
                startIcon={<i className='material-icons'>search</i>}
              >
                Buscar
              </Button>
            </div>
          </div>
        }
      />
      {hasResultados && alumnosResultado.length > 0 && (
        <section className={css.seccion}>
          <h3 className={css.titulo}>{alumnosResultado.length} alumnos</h3>
          <InformationTable
            details={[
              { title: 'Nombre', key: 'nombre' },
              { title: 'Centro', key: 'centro' },
              {
                title: 'Teléfono',
                key: 'telefono',
                visibility: 'lg',
                sortDisabled: true
              },
              {
                title: 'E-mail',
                key: 'email',
                visibility: 'lg',
                sortDisabled: true
              }
            ]}
            data={alumnosResultado.map(alumno => ({
              key: alumno.id,
              id: alumno.id,
              nombre: alumno.nombre,
              centro: alumno.centro || '--',
              telefono: alumno.telefonos?.[0]?.telefono || '--',
              email: alumno.emails?.[0]?.email || '--'
            }))}
            isFetching={isLoadingAlumnos}
            onRowClick={row => navigate(`/alumnos/${row.id}/ficha`)}
          />
        </section>
      )}
      {hasResultados && leadsResultado.length > 0 && (
        <section className={css.seccion}>
          <h3 className={css.titulo}>{leadsResultado.length} leads</h3>
          <InformationTable
            details={[
              { title: 'Contacto', key: 'contacto' },
              { title: 'Centro', key: 'centro' },
              { title: 'Estado', key: 'estado' },
              {
                title: 'Teléfono',
                key: 'telefono',
                visibility: 'lg',
                sortDisabled: true
              },
              {
                title: 'E-mail',
                key: 'email',
                visibility: 'lg',
                sortDisabled: true
              }
            ]}
            data={leadsResultado.map(lead => ({
              key: lead.id,
              id: lead.id,
              contacto: lead.contacto,
              centro: lead.centro || '--',
              estado: getEstado(lead.estado) || '--',
              telefono: lead.telefonos?.[0]?.telefono || '--',
              email: lead.email || '--'
            }))}
            isFetching={isLoadingLeads}
            onRowClick={row => navigate(`/leads/${row.id}/ficha`)}
          />
        </section>
      )}
    </PageLoading>
  )
}

export default Buscador
